import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getUser, sendChangeCompany, signIn as sendSignInRequest } from '../api/auth';
import type { AuthContextType, User } from '../types';
import { AuthUser, SignInAuthResult } from "../types";
import jwt from 'jwt-decode'
import { Environment } from '../types/enums';
import { endDynatraceSession } from '../utils/dynatrace';

function AuthProvider(props: React.PropsWithChildren<unknown>) {
    const [user, setUser] = useState<User>();
    const [loading, setLoading] = useState(true);
    const [logoutChannel] = useState<BroadcastChannel>(new BroadcastChannel('logout'));
    const [signInChannel] = useState<BroadcastChannel>(new BroadcastChannel('signIn'));

    useEffect(() => {
        (async function () {
            addSignInBroadcastChannelHandler();
            addLogoutBroadcastChannelHandler();
            const result = await getUser();
            if (result.isOk) {
                const authUser: AuthUser = jwt(result.data as string);
                if (Date.now() / 1000 > authUser.exp) {
                    signOut(true);
                    setLoading(false);
                    return;
                }

                setUserFromToken(result.data as string);
            }

            setLoading(false);
        })();
    }, []);

    function setUserFromToken(token: string) {
        const authUser: AuthUser = jwt(token);
        if (!authUser.a) {
            const user: User = {
                userName: authUser.u,
                companyId: Number(authUser.cid),
                companyTypeId: Number(authUser.ct),
                environment: authUser.e === 'S' ? Environment.Sage : Environment.HausPro
            };
            setUser(user);
        }
    }

    const signIn = useCallback(async (environment: string, userName: string, password: string): Promise<SignInAuthResult> => {
        const result = await sendSignInRequest(environment, userName, password);
        if (result.isOk) {
            if (result.changePasswordToken) {
                return {
                    isOk: true,
                    isAdmin: false,
                    token: '',
                    changePasswordToken: result.changePasswordToken,
                    isPasswordExpired: result.isPasswordExpired,
                };
            }

            const aUser: AuthUser = jwt(result.data as string);
            if (aUser.a) {
                return { isOk: true, isAdmin: true, token: result.data };
            }

            return { isOk: true, isAdmin: false, token: result.data };
        } else {
            return { isOk: false, message: result.message };
        }
    }, []);

    const changeCompany = useCallback(async (companyId: number) => {
        const result = await sendChangeCompany(companyId);
        externalLogin(result.data as string);
    }, []);

    const signOut = useCallback((sendMessage: boolean) => {
        endDynatraceSession();
        localStorage.removeItem('access_token');
        if (sendMessage) {
            logoutChannel.postMessage("Logout");
        }
        setUser(undefined);
        logoutChannel.close();
        window.location.href = window.location.origin + "/";
    }, []);

    const addLogoutBroadcastChannelHandler = useCallback(() => {
        logoutChannel.onmessage = () => {
            signOut(false);
        }
    }, []);

    const addSignInBroadcastChannelHandler = useCallback(() => {
        signInChannel.onmessage = () => {
            window.location.reload();
        }
    }, []);


    const externalLogin = useCallback((token: string) => {
        localStorage.setItem('access_token', token);
        setUserFromToken(token);
        signInChannel.postMessage("signIn");
    }, []);

    return (
        <AuthContext.Provider value={{ user, changeCompany, signIn, signOut, externalLogin, loading }} {...props} />
    );
}

const AuthContext = createContext<AuthContextType>({ loading: false } as AuthContextType);
const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth }
